import {
  ProfileBadgeTag,
  ProfileBadgeTagVariant,
} from '#app/components/ProfileBadgeTag/ProfileBadgeTag.js';
import {
  expertPartnerships,
  SupportedExpertPartner,
} from '@contra/expert-partnerships';
import {
  IconSvgProps,
  SocialBotstacksIcon,
  SocialBubbleIcon,
  SocialBuildshipIcon,
  SocialDescriptIcon,
  SocialFlutterflowIcon,
  SocialFramerColorIcon,
  SocialGlorifyIcon,
  SocialHeygenBlackIcon,
  SocialInstantIcon,
  SocialJitterAltIcon,
  SocialKittlIcon,
  SocialLottiefilesIcon,
  SocialRelumeIcon,
  SocialReploIcon,
  SocialRiveBlackIcon,
  SocialSplineIcon,
  SocialWebflowColorIcon,
  SocialWebstudioIcon,
  SocialWixstudioIcon,
  SocialXanoIcon,
} from '@contra/icons/icons';
import { ToolFigma } from '@contra/icons/tools';
import { FC } from 'react';

const EXPERT_BADGE_VARIANTS: Record<
  SupportedExpertPartner,
  ProfileBadgeTagVariant
> = {
  BOTSTACKS_EXPERT: 'white',
  BUBBLE_EXPERT: 'blue',
  BUILDSHIP_EXPERT: 'blue',
  CONTRA_EXPERT_TEST: 'white',
  DESCRIPT_EXPERT: 'white',
  FLUTTERFLOW_EXPERT: 'white',
  FRAMER_EXPERT: 'lavender',
  GLORIFY_EXPERT: 'white',
  HEY_GEN_EXPERT: 'white',
  INSTANT_EXPERT: 'blue',
  JITTER_EXPERT: 'white',
  KITTL_EXPERT: 'green',
  LOTTIEFILES_X_FIGMA_EXPERT: 'white',
  LOTTIEFILES_X_FRAMER_EXPERT: 'white',
  LOTTIEFILES_X_WEBFLOW_EXPERT: 'white',
  RELUME_EXPERT: 'salmon',
  REPLO_EXPERT: 'white',
  RIVE_EXPERT: 'pink',
  SPLINE_EXPERT: 'pink',
  WEBFLOW_EXPERT: 'white',
  WEBSTUDIO_EXPERT: 'pink',
  WIX_STUDIO_EXPERT: 'white',
};

export const EXPERT_BADGE_ICON_COLORS: Record<
  SupportedExpertPartner,
  string | undefined
> = {
  BOTSTACKS_EXPERT: undefined,
  BUBBLE_EXPERT: undefined,
  BUILDSHIP_EXPERT: undefined,
  CONTRA_EXPERT_TEST: undefined,
  DESCRIPT_EXPERT: undefined,
  FLUTTERFLOW_EXPERT: undefined,
  FRAMER_EXPERT: undefined,
  GLORIFY_EXPERT: undefined,
  HEY_GEN_EXPERT: undefined,
  INSTANT_EXPERT: undefined,
  JITTER_EXPERT: 'white',
  KITTL_EXPERT: undefined,
  LOTTIEFILES_X_FIGMA_EXPERT: undefined,
  LOTTIEFILES_X_FRAMER_EXPERT: undefined,
  LOTTIEFILES_X_WEBFLOW_EXPERT: undefined,
  RELUME_EXPERT: undefined,
  REPLO_EXPERT: undefined,
  RIVE_EXPERT: undefined,
  SPLINE_EXPERT: undefined,
  WEBFLOW_EXPERT: undefined,
  WEBSTUDIO_EXPERT: undefined,
  WIX_STUDIO_EXPERT: undefined,
};

export const EXPERT_BADGE_ICON_BACKGROUND_COLORS: Record<
  SupportedExpertPartner,
  string | undefined
> = {
  BOTSTACKS_EXPERT: undefined,
  BUBBLE_EXPERT: undefined,
  BUILDSHIP_EXPERT: undefined,
  CONTRA_EXPERT_TEST: undefined,
  DESCRIPT_EXPERT: undefined,
  FLUTTERFLOW_EXPERT: undefined,
  FRAMER_EXPERT: undefined,
  GLORIFY_EXPERT: undefined,
  HEY_GEN_EXPERT: undefined,
  INSTANT_EXPERT: undefined,
  JITTER_EXPERT: '#7A40ED',
  KITTL_EXPERT: undefined,
  LOTTIEFILES_X_FIGMA_EXPERT: undefined,
  LOTTIEFILES_X_FRAMER_EXPERT: undefined,
  LOTTIEFILES_X_WEBFLOW_EXPERT: undefined,
  RELUME_EXPERT: undefined,
  REPLO_EXPERT: undefined,
  RIVE_EXPERT: undefined,
  SPLINE_EXPERT: undefined,
  WEBFLOW_EXPERT: undefined,
  WEBSTUDIO_EXPERT: undefined,
  WIX_STUDIO_EXPERT: undefined,
};

export const EXPERT_BADGE_ICONS: Record<
  SupportedExpertPartner,
  FC<IconSvgProps>
> = {
  BOTSTACKS_EXPERT: SocialBotstacksIcon,
  BUBBLE_EXPERT: SocialBubbleIcon,
  BUILDSHIP_EXPERT: SocialBuildshipIcon,
  CONTRA_EXPERT_TEST: SocialXanoIcon,
  DESCRIPT_EXPERT: SocialDescriptIcon,
  FLUTTERFLOW_EXPERT: SocialFlutterflowIcon,
  FRAMER_EXPERT: SocialFramerColorIcon,
  GLORIFY_EXPERT: SocialGlorifyIcon,
  HEY_GEN_EXPERT: SocialHeygenBlackIcon,
  INSTANT_EXPERT: SocialInstantIcon,
  JITTER_EXPERT: SocialJitterAltIcon,
  KITTL_EXPERT: SocialKittlIcon,
  LOTTIEFILES_X_FIGMA_EXPERT: SocialLottiefilesIcon,
  LOTTIEFILES_X_FRAMER_EXPERT: SocialLottiefilesIcon,
  LOTTIEFILES_X_WEBFLOW_EXPERT: SocialLottiefilesIcon,
  RELUME_EXPERT: SocialRelumeIcon,
  REPLO_EXPERT: SocialReploIcon,
  RIVE_EXPERT: SocialRiveBlackIcon,
  SPLINE_EXPERT: SocialSplineIcon,
  WEBFLOW_EXPERT: SocialWebflowColorIcon,
  WEBSTUDIO_EXPERT: SocialWebstudioIcon,
  WIX_STUDIO_EXPERT: SocialWixstudioIcon,
};

type Props = {
  readonly badgeSize?: 'default' | 'small';
  readonly partnerNames: SupportedExpertPartner[];
  readonly showFullLabel?: boolean;
  readonly showOnlyPartnerName?: boolean;
  readonly source?: 'expert_list';
  readonly withColoredExpertBadges?: boolean;
};

export const ExpertBadges = ({
  badgeSize = 'default',
  partnerNames,
  showFullLabel = false,
  showOnlyPartnerName = false,
  source,
  withColoredExpertBadges = true,
}: Props) => {
  if (partnerNames.length === 0) {
    return null;
  }

  return (
    <>
      {partnerNames.map((partnerName) => {
        const expertPartnership = expertPartnerships[partnerName];

        if (
          partnerName === ('LOTTIEFILES_X_WEBFLOW_EXPERT' as const) ||
          partnerName === ('LOTTIEFILES_X_FIGMA_EXPERT' as const) ||
          partnerName === ('LOTTIEFILES_X_FRAMER_EXPERT' as const)
        ) {
          return (
            <ProfileBadgeTag
              ariaLabel={
                partnerName === 'LOTTIEFILES_X_WEBFLOW_EXPERT'
                  ? 'LottieFiles x Webflow Expert'
                  : partnerName === 'LOTTIEFILES_X_FIGMA_EXPERT'
                    ? 'LottieFiles x Figma Expert'
                    : 'LottieFiles x Framer Expert'
              }
              Icon={SocialLottiefilesIcon}
              iconBackgroundColor={
                EXPERT_BADGE_ICON_BACKGROUND_COLORS[partnerName]
              }
              iconColor={EXPERT_BADGE_ICON_COLORS[partnerName]}
              key={partnerName}
              label="LottieFiles"
              secondaryPartner={
                partnerName === 'LOTTIEFILES_X_WEBFLOW_EXPERT'
                  ? {
                      icon: <SocialWebflowColorIcon size={16} />,
                      label: 'Webflow',
                    }
                  : partnerName === 'LOTTIEFILES_X_FIGMA_EXPERT'
                    ? {
                        icon: <ToolFigma size={16} />,
                        label: 'Figma',
                      }
                    : {
                        icon: <SocialFramerColorIcon size={16} />,
                        label: 'Framer',
                      }
              }
              size={badgeSize}
              variant={
                withColoredExpertBadges
                  ? EXPERT_BADGE_VARIANTS[partnerName]
                  : 'white'
              }
            />
          );
        }

        const fullLabel =
          expertPartnership.name === 'flutterflow'
            ? source === 'expert_list'
              ? `Expert Coach`
              : `${expertPartnership.displayName} Coach`
            : `${expertPartnership.displayName} Expert`;

        return (
          <ProfileBadgeTag
            ariaLabel={fullLabel}
            Icon={EXPERT_BADGE_ICONS[partnerName]}
            iconBackgroundColor={
              EXPERT_BADGE_ICON_BACKGROUND_COLORS[partnerName]
            }
            iconColor={EXPERT_BADGE_ICON_COLORS[partnerName]}
            key={partnerName}
            label={
              showOnlyPartnerName
                ? expertPartnership.displayName
                : badgeSize === 'small' || !showFullLabel
                  ? 'Expert'
                  : fullLabel
            }
            size={badgeSize}
            variant={
              withColoredExpertBadges
                ? EXPERT_BADGE_VARIANTS[partnerName]
                : 'white'
            }
          />
        );
      })}
    </>
  );
};
